<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import LiveVideoPlayer from '@/components/LiveTrivia/LiveVideoPlayer.vue'
import type { TLiveVideoPlaybackStatus } from '@/store/liveTrivia/state'
import type { TLiveHostMessage } from '@/store/liveTrivia/types'

const { videoPlaybackStatus, videoStream, hostMessages } = defineProps<{
    videoPlaybackStatus: TLiveVideoPlaybackStatus
    videoStream: MediaStream | null
    hostMessages: TLiveHostMessage[]
}>()

const isVideoMuted = defineModel<boolean>('isVideoMuted', { required: true })

const messagesElement = ref<HTMLElement | null>(null)

const formatMessageTime = (at: string) => new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

// Follow the newest message, the way a chat does
watch(() => hostMessages.length, async () => {
    await nextTick()
    const messagesEl = messagesElement.value
    if (messagesEl) {
        messagesEl.scrollTop = messagesEl.scrollHeight
    }
}, { immediate: true })
</script>

<template>
    <div class="live-side-panel">
        <!-- Stays at the top so the host is always in view, whatever the question area is showing -->
        <LiveVideoPlayer
            v-if="videoPlaybackStatus !== 'off'"
            v-model:is-muted="isVideoMuted"
            class="live-side-panel__video"
            :stream="videoStream"
            :playback-status="videoPlaybackStatus"
        />
        <h2 class="live-side-panel__heading">From the host</h2>
        <div
            ref="messagesElement"
            class="live-side-panel__messages"
            role="log"
            aria-live="polite"
            aria-label="Messages from the host"
        >
            <p v-if="!hostMessages.length" class="live-side-panel__empty">
                Messages from the host show up here.
            </p>
            <div
                v-for="hostMessage in hostMessages"
                :key="hostMessage.at"
                class="live-side-panel__message"
            >
                <span class="live-side-panel__message-text">{{ hostMessage.text }}</span>
                <time class="live-side-panel__message-time" :datetime="hostMessage.at">
                    {{ formatMessageTime(hostMessage.at) }}
                </time>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.live-side-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    padding: 12px;
    box-sizing: border-box;
    color: $white;

    &__video {
        flex-shrink: 0;
        margin-bottom: 12px;
    }

    &__heading {
        flex-shrink: 0;
        margin: 0 0 8px;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        color: rgba($white, 0.7);
    }

    &__messages {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__empty {
        margin: 0;
        font-size: 14px;
        line-height: 20px;
        color: rgba($white, 0.6);
    }

    &__message {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 8px 10px;
        border-radius: 6px;
        background: rgba($white, 0.08);
    }

    &__message-text {
        font-size: 15px;
        line-height: 21px;
        overflow-wrap: anywhere;
    }

    &__message-time {
        align-self: flex-end;
        font-size: 12px;
        color: rgba($white, 0.55);
    }
}
</style>
